import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import Cropper from "react-easy-crop";
import "./AdminProfile.css";
import Button from "../../components/Button/Button";
import { useAlertDialog } from "../../components/ConfirmDialog/ConfirmDialogProvider";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import getCroppedImg from "../../utils/getCroppedImg";
import compressImageToMaxSize from "../../utils/compressImageToMaxSize";
import withCacheBust from "../../utils/withCacheBust";
import profilePic from "../../assets/images/profile_image.png";

const AdminProfile = () => {
  const showAlert = useAlertDialog();
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  // Fields to edit
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [description, setDescription] = useState("");

  // Holds the returned `profilePictureUrl` from the server
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("avatar");

  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const cropSizeValue = isMobile ? { width: 200, height: 200 } : { width: 459.102, height: 428.719 };
  const aspectValue = isMobile ? 1 : 459.102 / 428.719;

  // If no token, you could redirect or show a message
  const idToken = localStorage.getItem("idToken");

  useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/profile`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        const data = response.data;
        setAdminData(data);

        // Pre-fill form fields
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setPhoneNumber(data.phone_number || "");
        setDescription(data.description || "");

        // The backend should return a field like "profilePictureUrl"
        setProfilePictureUrl(data.profilePictureUrl || null);
      } catch (err) {
        console.error("Error fetching admin profile:", err);
      } finally {
        setLoading(false);
      }
    };

    if (idToken) {
      fetchAdminProfile();
    } else {
      setLoading(false);
    }
  }, [idToken]);

  const handleUpdateProfile = async () => {
    if (isSavingProfile) return;

    try {
      setIsSavingProfile(true);
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/profile`,
        {
          firstName,
          lastName,
          phoneNumber,
          description,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      await showAlert({
        title: 'PROFILE',
        message: 'Profile updated successfully!',
      });
      setEditMode(false);
    } catch (err) {
      console.error("Error updating admin profile:", err);
      await showAlert({
        title: 'PROFILE',
        message: 'Failed to update profile.',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const onCropComplete = useCallback((_, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const onMediaLoaded = useCallback((mediaSize) => {
    const w = mediaSize.naturalWidth || mediaSize.width;
    const h = mediaSize.naturalHeight || mediaSize.height;
    const minZ = Math.max(cropSizeValue.width / w, cropSizeValue.height / h, 1);
    setMinZoom(minZ);
    setZoom((z) => (z < minZ ? minZ : z));
  }, [cropSizeValue.height, cropSizeValue.width]);

  const handleFileChange = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) {
      await showAlert({
        title: 'PROFILE',
        message: 'Only JPEG, PNG, GIF, WEBP allowed.',
      });
      return;
    }
    setSelectedFileName(file.name.replace(/\.[^.]+$/, "") || "avatar");
    const reader = new FileReader();
    reader.onloadend = () => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropImageSrc(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmCrop = async () => {
    if (isUploadingPicture) return;
    if (!cropImageSrc || !croppedAreaPixels) {
      await showAlert({
        title: 'PROFILE',
        message: 'Set crop first.',
      });
      return;
    }

    try {
      setIsUploadingPicture(true);
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      const finalBlob = await compressImageToMaxSize(croppedBlob, 5 * 1024 * 1024);
      const croppedFile = new File([finalBlob], `${selectedFileName}.jpg`, { type: "image/jpeg" });

      const formData = new FormData();
      formData.append("profilePicture", croppedFile);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/profile/picture`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.profilePictureUrl) {
        const raw = response.data.profilePictureUrl;
        const versioned = withCacheBust(raw, Date.now());
        setProfilePictureUrl(versioned);
        await showAlert({
          title: 'PROFILE',
          message: 'Profile picture updated successfully!',
        });
      }
      setShowCropModal(false);
    } catch (err) {
      console.error("Error uploading admin profile picture:", err);
      await showAlert({
        title: 'PROFILE',
        message: 'Failed to upload profile picture.',
      });
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const backendUrl = process.env.REACT_APP_API_URL || "";

  let profileImageSrc = profilePic; // fallback if none
  if (profilePictureUrl) {
    profileImageSrc = profilePictureUrl.startsWith("http")
      ? profilePictureUrl
      : `${backendUrl}${profilePictureUrl}`;
  }

  if (loading) return <LoadingOverlay label="Loading admin profile" />;
  if (!idToken) return <p>No token found. Please log in.</p>;
  if (!adminData) return <p>No admin data found.</p>;

  return (
    <>
      {(isSavingProfile || isUploadingPicture) && (
        <LoadingOverlay
          label={isUploadingPicture ? 'Uploading profile picture' : 'Saving admin profile'}
        />
      )}
      <div className="admin-profile-container">
        <h2>Admin Profile</h2>
        <div className="profile-section">
          <div className="profile-image-section">
            <div className="admin-profile-image-wrapper">
              <img
                src={profileImageSrc}
                alt="Admin profile"
                className="admin-profile-image"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = profilePic;
                }}
              />
            </div>
            <label htmlFor="profilePictureInput">Change Picture</label>
            <input
              id="profilePictureInput"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
          <div className="profile-details-section">
            {editMode ? (
              <>
                <label>First Name</label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  type="text"
                />

                <label>Last Name</label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  type="text"
                />

                <label>Phone Number</label>
                <input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  type="text"
                />

                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />

                <Button
                  text={isSavingProfile ? "Saving..." : "Save"}
                  onClick={handleUpdateProfile}
                  disabled={isSavingProfile || isUploadingPicture}
                />
                <Button
                  text="Cancel"
                  onClick={() => setEditMode(false)}
                  disabled={isSavingProfile || isUploadingPicture}
                />
              </>
            ) : (
              <>
                <div className="profile-details-row">
                  <span className="profile-details-label">Name:</span>
                  <span className="profile-details-value">
                    {adminData.first_name} {adminData.last_name}
                  </span>
                </div>
                <div className="profile-details-row">
                  <span className="profile-details-label">Phone:</span>
                  <span className="profile-details-value">
                    {adminData.phone_number || "—"}
                  </span>
                </div>
                <div className="profile-details-row">
                  <span className="profile-details-label">Description:</span>
                  <span className="profile-details-value">
                    {adminData.description || "—"}
                  </span>
                </div>
                <Button
                  text="Edit Profile"
                  onClick={() => setEditMode(true)}
                  disabled={isSavingProfile || isUploadingPicture}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {showCropModal && (
        <div className="crop-modal">
          <div className="crop-editor">
            <div className="crop-container">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                minZoom={minZoom}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                onMediaLoaded={onMediaLoaded}
                showGrid={false}
                restrictPosition
                aspect={aspectValue}
                cropSize={cropSizeValue}
                cropShape="rect"
                style={{
                  cropAreaStyle: { border: 'none', boxShadow: 'none', background: 'transparent' },
                  containerStyle: { background: '#fff' },
                }}
              />
            </div>
            <div className="zoom-slider-container" aria-label="Zoom control">
              <span className="zoom-label zoom-label-plus">+</span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="zoom-slider"
              />
              <span className="zoom-label zoom-label-minus">-</span>
            </div>
          </div>

          <div className="crop-controls">
            <Button
              text={isUploadingPicture ? "Uploading..." : "Confirm Crop"}
              onClick={handleConfirmCrop}
              disabled={isUploadingPicture || isSavingProfile}
            />
            <Button
              text="Cancel"
              onClick={() => setShowCropModal(false)}
              disabled={isUploadingPicture || isSavingProfile}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AdminProfile;
